import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'
import { executePolling } from 'core/utils/promise/executePolling'

import { hasRunningProcess, type Proposal } from '../../domain/Proposal'
import { getProposal } from '../../use-cases/getProposal'
import { submitProposal } from '../../use-cases/submitProposal'
import { toggleAutoSubmitProposal } from '../../use-cases/toggleAutoSubmitProposal'
import { withdrawProposal } from '../../use-cases/withdrawProposal'
import { useRfpContext } from '../contexts/RfpContext'

type State = {
  proposal?: Proposal
  proposalStatus: RequestStatus
  submitStatus: RequestStatus
  withdrawStatus: RequestStatus
  autoSubmitStatus: RequestStatus
  isAutoSubmitEnabled: boolean
}

const initialState = {
  proposalStatus: RequestStatus.Initial,
  submitStatus: RequestStatus.Initial,
  withdrawStatus: RequestStatus.Initial,
  autoSubmitStatus: RequestStatus.Initial,
  isAutoSubmitEnabled: false,
}

const POLLING_INTERVAL = 5000

export function useProposal() {
  const { setLaneProposalIds } = useRfpContext()
  const [
    {
      proposal,
      proposalStatus,
      submitStatus,
      withdrawStatus,
      autoSubmitStatus,
      isAutoSubmitEnabled,
    },
    setState,
  ] = useState<State>(initialState)

  const isSubmittingProposal = submitStatus === RequestStatus.Pending
  const isWithdrawingProposal = withdrawStatus === RequestStatus.Pending
  const isAutoSubmittingProposal = autoSubmitStatus === RequestStatus.Pending

  const fetchProposal = useCallback(
    async (id: string, { signal }: AbortController) => {
      try {
        setState(previous => ({ ...previous, proposalStatus: RequestStatus.Pending }))

        const proposalResult = await getProposal(id, null, { signal })

        setLaneProposalIds(
          proposalResult.lanes
            .filter(lane => Boolean(lane.laneProposal))
            .map(lane => lane.laneProposal!.id)
        )

        setState(previous => ({
          ...previous,
          proposal: proposalResult,
          proposalStatus: RequestStatus.Done,
          isAutoSubmitEnabled: proposalResult.autoSubmit,
        }))
      } catch (error) {
        if (!signal.aborted) {
          setState(previous => ({
            ...previous,
            proposal: undefined,
            proposalStatus: RequestStatus.Error,
          }))
        }
      }
    },
    [setLaneProposalIds]
  )

  const onSubmitProposal = useCallback(
    async (id: string) => {
      if (isSubmittingProposal) return

      try {
        setState(previous => ({ ...previous, submitStatus: RequestStatus.Pending }))

        await submitProposal(id)

        setState(previous => ({ ...previous, submitStatus: RequestStatus.Done }))
      } catch {
        setState(previous => ({ ...previous, submitStatus: RequestStatus.Error }))

        throw new Error('Could not submit the proposal')
      }
    },
    [isSubmittingProposal]
  )

  const onWithdrawProposal = useCallback(
    async (id: string) => {
      if (isWithdrawingProposal) return

      try {
        setState(previous => ({ ...previous, withdrawStatus: RequestStatus.Pending }))

        await withdrawProposal(id)

        setState(previous => ({ ...previous, withdrawStatus: RequestStatus.Done }))
      } catch {
        setState(previous => ({ ...previous, withdrawStatus: RequestStatus.Error }))

        throw new Error('Could not withdraw the proposal')
      }
    },
    [isWithdrawingProposal]
  )

  const onToggleAutoSubmitProposal = useCallback(
    async (id: string, newAutoSubmit: boolean, { signal }: AbortController) => {
      if (isAutoSubmittingProposal) return

      try {
        setState(previous => ({ ...previous, autoSubmitStatus: RequestStatus.Pending }))

        const { autoSubmit } = await toggleAutoSubmitProposal(id, newAutoSubmit, { signal })

        setState(previous => ({
          ...previous,
          autoSubmitStatus: RequestStatus.Done,
          isAutoSubmitEnabled: autoSubmit,
        }))
      } catch {
        setState(previous => ({ ...previous, autoSubmitStatus: RequestStatus.Error }))

        throw new Error('Could not toggle proposal auto submit')
      }
    },
    [isAutoSubmittingProposal]
  )

  const pollProposal = useCallback(
    async (id: string, { signal }: AbortController) => {
      const proposalResult = await executePolling(() => getProposal(id, null, { signal }), {
        intervalMs: POLLING_INTERVAL,
        condition: proposal => !hasRunningProcess(proposal),
      })

      if (proposal) {
        setState(previous => ({
          ...previous,
          proposal: proposalResult,
        }))
      }
    },
    [proposal]
  )

  return {
    proposal,
    proposalStatus,
    isSubmittingProposal,
    isWithdrawingProposal,
    isAutoSubmitEnabled,
    isAutoSubmittingProposal,
    fetchProposal,
    onSubmitProposal,
    onWithdrawProposal,
    onToggleAutoSubmitProposal,
    pollProposal,
  }
}
